/* Forward animation engagement onto Shopify's customer-event bus.
 *
 * The simulators are framed from the Shopify CDN, a different origin from the
 * storefront, so the page cannot see inside them. Each animation reports what
 * it can by postMessage; this picks those up and republishes them through
 * Shopify.analytics.publish, where any pixel can subscribe:
 *
 *     analytics.subscribe('sbc_animation_fault_selected', (e) => { ... })
 *
 * Nothing here stores or displays anything. It is the plumbing between the
 * animation and whatever destination gets wired up later -- add a pixel and the
 * history starts from that day, with no further changes to any animation.
 *
 * Loaded once from layout/theme.liquid rather than per section, because the
 * battery page frames three animations at once and a per-section include would
 * register three listeners and triple-count every event.
 */
(function () {
  'use strict';

  var PREFIX = 'sbc_animation_';
  var MAX_KEYS = 10;
  var MAX_LEN = 100;

  function animationFrames() {
    return document.querySelectorAll('iframe[src]');
  }

  /* Senders are identified by window, NOT by origin.
   *
   * Shopify serves theme HTML assets with
   *     content-security-policy: sandbox allow-scripts allow-forms ...
   * and no allow-same-origin, so every animation frame runs in an opaque
   * origin and arrives here with event.origin === "null" -- a literal string,
   * never the CDN host. An origin allowlist therefore rejects everything, and
   * rejects it silently: the page works, the animation works, and the events
   * just never appear. That reads as "nobody uses the tool" rather than as a
   * broken pipe, which is the most expensive way for analytics to fail.
   *
   * Comparing against the contentWindow of a frame actually on this page is
   * both immune to that and strictly stronger than matching a host: a window
   * reference cannot be spoofed by an unrelated frame claiming an origin. */
  function isOurFrame(win) {
    if (!win) return false;
    var frames = animationFrames();
    for (var i = 0; i < frames.length; i++) {
      if (frames[i].contentWindow === win) return true;
    }
    return false;
  }

  function clean(name) {
    return String(name).replace(/[^a-z0-9_]/gi, '_').toLowerCase().slice(0, 40);
  }

  /* Copy only scalars, and only a bounded number of them. The message crosses
     an origin boundary, so its shape is not ours to trust however friendly the
     sender looks. */
  function payloadFrom(message) {
    var out = { animation: clean(message.animation || 'unknown') };
    var data = message.data;
    if (!data || typeof data !== 'object') return out;
    var keys = Object.keys(data).slice(0, MAX_KEYS);
    for (var i = 0; i < keys.length; i++) {
      var value = data[keys[i]];
      var t = typeof value;
      if (t === 'string') out[clean(keys[i])] = value.slice(0, MAX_LEN);
      else if (t === 'number' || t === 'boolean') out[clean(keys[i])] = value;
    }
    return out;
  }

  /* Respect the visitor's analytics choice.
   *
   * Publishing to the event bus does not by itself store anything on the
   * device, so this is not what makes the page lawful -- the consent banner and
   * Shopify's own cookie gating do that. But the whole point of this plumbing is
   * that a pixel gets attached later, and a pixel can set cookies and call out
   * to a third party. Gating here means that when that day comes, the events
   * were never flowing for someone who declined, rather than it depending on
   * whoever configures the pixel remembering to check.
   *
   * When the Customer Privacy API is absent the answer is yes: no consent
   * framework is loaded, so nothing is being withheld and a default of "no"
   * would silently disable telemetry everywhere for no gain. */
  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return true;
      return privacy.analyticsProcessingAllowed() !== false;
    } catch (e) {
      return true;
    }
  }

  window.addEventListener('message', function (event) {
    var message = event.data;
    if (!message || message.source !== 'sbc-animation') return;
    if (typeof message.event !== 'string' || !message.event) return;
    if (!isOurFrame(event.source)) return;
    if (!analyticsAllowed()) return;

    var analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return;

    try {
      analytics.publish(PREFIX + clean(message.event), payloadFrom(message));
    } catch (e) {
      /* Telemetry is never worth breaking a page over. */
    }
  }, false);

  /* An animation finishes loading and reports "opened" immediately. That can
     easily happen before this script has run -- it is a separate request, and
     iframe load order is not ours to control -- and a listener registered
     afterwards never sees the message that already went past.

     So the frames wait to be told someone is listening. Saying it once is not
     enough either: a frame that finished before this script ran will never fire
     a load event for us, so announce on load AND blind-fire a couple of times
     after. The animation side keeps "opened" to one event however many of these
     arrive. */
  function announce() {
    var frames = animationFrames();
    for (var i = 0; i < frames.length; i++) {
      if (!frames[i].contentWindow) continue;
      try {
        frames[i].contentWindow.postMessage({ source: 'sbc-bridge', event: 'ready' }, '*');
      } catch (e) { /* frame not ready yet; a later announce will catch it */ }
    }
  }

  function start() {
    var frames = animationFrames();
    for (var i = 0; i < frames.length; i++) {
      frames[i].addEventListener('load', announce, false);
    }
    announce();
    setTimeout(announce, 400);
    setTimeout(announce, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, false);
  } else {
    start();
  }
})();
